import { Controller } from "@hotwired/stimulus"

const EMPLOYEE_STEPS = [1, 10, 25, 50, 100, 150, 250, 500, 1000, 2000, 5000]

// Mimics Users::NameGenerator#sanitize from Ruby backend
function generateUsername(input: string): string {
  let name = input.toLowerCase().trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9\s-]/g, "")
  const strip = ["und", "gmbh", "co", "kg", "ag"]
  for (const word of strip) {
    name = name.replace(new RegExp(`([-\\s])${word}(\\s|-|$)`, "g"), " ")
  }
  return name.trim().replace(/\s+/g, "_").replace(/-+/g, "_").replace(/^_|_$/g, "")
}

export default class extends Controller {
  static targets = [
    "membershipBtn",
    "employeeSlider",
    "employeeValue",
    "resultPrice",
    "resultPriceMonthly",
    "breakdown",
    "submitBtn",
    "form",
    "successMessage",
    "errorMessage",
    "errorList",
    "companyName",
  ]

  declare readonly membershipBtnTargets: HTMLButtonElement[]
  declare readonly employeeSliderTarget: HTMLInputElement
  declare readonly employeeValueTarget: HTMLElement
  declare readonly resultPriceTarget: HTMLElement
  declare readonly resultPriceMonthlyTarget: HTMLElement
  declare readonly breakdownTarget: HTMLElement
  declare readonly submitBtnTarget: HTMLButtonElement
  declare readonly formTarget: HTMLFormElement
  declare readonly successMessageTarget: HTMLElement
  declare readonly errorMessageTarget: HTMLElement
  declare readonly errorListTarget: HTMLElement
  declare readonly companyNameTarget: HTMLInputElement

  private membershipType = "partner"
  private employeeCount = 150
  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  connect() {
    this.employeeSliderTarget.value = String(this.stepFromCount(this.employeeCount))
    this.updateEmployeeDisplay()
    this.fetchQuote()
  }

  selectMembership(event: Event) {
    const btn = event.currentTarget as HTMLButtonElement
    const type = btn.dataset.membership
    if (!type) return

    this.membershipType = type
    this.membershipBtnTargets.forEach(b => {
      b.classList.toggle("active", b.dataset.membership === type)
    })
    this.fetchQuote()
  }

  slideEmployee() {
    const stepIndex = parseInt(this.employeeSliderTarget.value, 10)
    this.employeeCount = EMPLOYEE_STEPS[stepIndex] || 150
    this.updateEmployeeDisplay()
    this.debouncedFetchQuote()
  }

  async submitOffer(event: Event) {
    event.preventDefault()
    this.submitBtnTarget.disabled = true
    this.hideMessages()

    const formData = new FormData(this.formTarget)
    const body = {
      offer: {
        company_name: formData.get("company_name"),
        person_first_name: formData.get("person_first_name"),
        person_last_name: formData.get("person_last_name"),
        email: formData.get("email"),
        phone: formData.get("phone"),
        membership_model: this.membershipType,
        employee_count: this.employeeCount,
      }
    }

    try {
      const response = await fetch("/registration/offers", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify(body),
      })

      if (response.ok) {
        this.successMessageTarget.hidden = false
        this.formTarget.reset()
      } else {
        const data = await response.json()
        this.showErrors(data.errors || ["Ein Fehler ist aufgetreten."])
      }
    } catch {
      this.showErrors(["Verbindungsfehler. Bitte versuchen Sie es erneut."])
    } finally {
      this.submitBtnTarget.disabled = false
    }
  }

  private updateEmployeeDisplay() {
    const count = this.employeeCount
    this.employeeValueTarget.textContent = count >= 5000 ? "5.000+" : count.toLocaleString("de-DE")
  }

  private stepFromCount(count: number): number {
    for (let i = EMPLOYEE_STEPS.length - 1; i >= 0; i--) {
      if (EMPLOYEE_STEPS[i] <= count) return i
    }
    return 0
  }

  private debouncedFetchQuote() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchQuote(), 300)
  }

  private organisationName(): string {
    return this.companyNameTarget?.value || ""
  }

  private async fetchQuote() {
    const orgName = this.organisationName()
    try {
      const response = await fetch("/registration/quote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({
          employee_count: this.employeeCount,
          membership_type: this.membershipType,
          smartlinks_possible: true,
          addons: [],
          organisation_name: orgName,
          username: generateUsername(orgName),
          start_date: "",
          promotion_code: "",
          akquisition_channel: "",
        }),
      })
      const data = await response.json()
      this.updatePriceDisplay(data)
    } catch (error) {
      console.error("Quote fetch failed:", error)
    }
  }

  private updatePriceDisplay(data: any) {
    const netto = data.totalPrice?.netto ?? 0
    const annual = netto.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const monthly = Math.round(netto / 12).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    this.resultPriceTarget.innerHTML = `<span class="calc__result-price-cur">&euro;</span>${annual}`
    this.resultPriceMonthlyTarget.textContent = `${monthly} € / Monat`

    if (data.lineItems) {
      const rows = data.lineItems.map((item: any) => {
        return `<div class="calc__breakdown-row">
          <span>${item.position}</span>
          <span>${item.nettoFormatted || (item.netto ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"}</span>
        </div>`
      }).join("")
      const totalRow = `<div class="calc__breakdown-row total">
        <span>Gesamt (netto/Jahr)</span>
        <span>${annual} €</span>
      </div>`
      this.breakdownTarget.innerHTML = rows + totalRow
    }
  }

  private showErrors(errors: string[]) {
    this.errorMessageTarget.hidden = false
    this.errorListTarget.innerHTML = errors.map(e => `<li>${e}</li>`).join("")
  }

  private hideMessages() {
    this.successMessageTarget.hidden = true
    this.errorMessageTarget.hidden = true
  }

  private csrfToken(): string {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
  }
}
